import React, { Suspense, useEffect, useRef, useState } from "react";
// Importing components and services
import DataLoader from "../../components/FinalMolGrid/ui/dataLoader/DataLoader";
import FormCreator from "../../components/forms/FormCreator";
import Buttons from "../../components/ui/button/Buttons";
import FinalMolGrid from "../../components/FinalMolGrid/FinalMolGrid";
import CenterModel from "../../components/ui/centerModel/CenterModel";
import { useLazyGetAllDropDownsForTaskListQuery, } from "../../app/services/PaymentReconciliation/PaymentReconciliationApi";
import { setAllDropDownFieldsBatch } from "../../utils/FormFields/FieldsSetting/SetFieldSetting";
import { paginationObj } from "../../utils/Constants";
import { WorkGroupMappingFormData } from "./data/WorkgroupMapping.Data";
import { WorkgroupMappingList } from "./data/WorkgroupMappingList";
import AddIndividualRecord from "./components/AddIndividualRecord";
import { useGetWorkGroupMappingListMutation, useAddEditChapterDetailsMutation, useDeleteWorkgroupMappingByIdMutation, useGetExcoMemberListMutation, useDeleteEXCOMembersMutation, } from "../../app/services/CommiteeWorkGroup/CommitteWorkGroupServiceApi";
import SwalAlert from "../../services/swalService/SwalService";
import { taskListDropDownsConfig } from "./utils/setDropdownFields";
import { SelectIndividualMemberData } from "./data/SelectIndividualMember.Data";
import { onResetForm } from "../../utils/FormFields/ResetForm/handleResetForm";
import { removeValidationAndDisableFields, addValidationAndEnableFields } from "../../utils/FormFields/RemoveFields/handleRemoveFields";
import { getAuditContext } from "../../lib/authenticationLibrary";

const CommitteeWorkgroupMappingForm = () => {
  // Destructuring success, error, and confirm alerts from SwalAlert service
  const { success, error, confirm } = SwalAlert();
  const audit = getAuditContext();

  // Refs for handling form and modal instances
  const committeWorkGroupRef = useRef();
  const addCommitteeWorkGroupRef = useRef();
  const addIndividualRef = useRef();
  const selectIndividualListRef = useRef();

  /* State Management */
  const [addWorkGroupForm, setAddWorkGroupForm] = useState(WorkGroupMappingFormData); // addWorkGroup form Data
  const [workGroupListData, setWorkGroupListData] = useState(); // Stores the workgroup list
  const [isEditModalOpen, SetIsEditModalOpen] = useState(false); // Toggles edit modal
  const [isAddModalOpen, SetIsAddModalOpen] = useState(false); // Toggles add modal
  const [isEditIndividual, SetIsEditIndividual] = useState(false);
  const [workgroupMappingId, setWorkGroupMemberId] = useState(0); // Tracks selected workgroup mapping ID
  const [editIndividualMemberDetails, SetEditIndividualMemberDetails] = useState(null); // selected edit indidvidual members
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    ...paginationObj,
    orderByColumn: "",
    orderFlag: 0,
    searchText: null || "",
    searchParentName: null || "",
    searchGroupName: null || "",
    searchGroupType: null || "",
  });

  const addWorkGroupFormInitState = {
    groupName: null,
    groupTypeId: null,
    isGroupTypeIdDisabled: false,
    // parentTypeId: null,
  };

  // API Mutations and Lazy Queries
  const [getAllWorkgropMapping, { isLoading: workgropMappingIsLoading, isSuccess: workgropMappingIsSuccess, data: workgropMappingListData }] = useGetWorkGroupMappingListMutation();
  const [addEditChapter, { isLoading: addEditChapterLoading, isSuccess: addEditChapterSuccess, data: addEditChapterData, }] = useAddEditChapterDetailsMutation();
  const [deleteChapter, { isLoading: deleteChapterLoading, isSuccess: deleteChapterSuccess, data: deleteChapterData }] = useDeleteWorkgroupMappingByIdMutation();
  const [getTaskListDropDown, { isLoading: taskListDropDownIsLoading, isSuccess: taskListDropDownIsSuccess, data: taskListDropDownData }] = useLazyGetAllDropDownsForTaskListQuery();
  const [selectIndividualList, { isLoading: isSelectIndividualLoading, isSuccess: isSelectIndividualSuccess, data: selectIndividualListResponse }] = useGetExcoMemberListMutation();
  const [deleteIndividualRecords, { isLoading: isDeleteRecordLoading, isSuccess: isDeleteRecordSuccess, data: deleteRecordResponse }] = useDeleteEXCOMembersMutation();

  useEffect(() => {
    getTaskListDropDown(); // Fetches dropdown data
    getAllWorkgropMapping(pagination); // Fetches the workgroup mapping list
  }, []);

  useEffect(() => {
    if (!isDeleteRecordLoading && isDeleteRecordSuccess && deleteRecordResponse) {
      let request = {
        id: workgroupMappingId,
        ...pagination,
      };
      selectIndividualList(request);
      success("Delete successfully", "Success");
    }
  }, [isDeleteRecordLoading, isDeleteRecordSuccess, deleteRecordResponse]);

  // Populating dropdown fields once the dropdown data is fetched
  useEffect(() => {
    if (!taskListDropDownIsLoading && taskListDropDownIsSuccess && taskListDropDownData) {
      setAllDropDownFieldsBatch(WorkGroupMappingFormData, taskListDropDownsConfig, taskListDropDownData);
    }
  }, [taskListDropDownIsLoading, taskListDropDownIsSuccess, taskListDropDownData]);

  // Handling fetched workgroup mapping data
  useEffect(() => {
    if (!workgropMappingIsLoading && workgropMappingIsSuccess && workgropMappingListData) {
      setWorkGroupListData(workgropMappingListData.itemList);
      setPagination((prev) => ({
        ...prev,
        totalCount: workgropMappingListData.totalCount,
      }));
    }
  }, [workgropMappingIsLoading, workgropMappingIsSuccess, workgropMappingListData]);

  // Handling Add/Edit API response
  useEffect(() => {
    if (!addEditChapterLoading && addEditChapterSuccess && addEditChapterData) {
      if (!addEditChapterData.hasError) {
        if (workgroupMappingId > 0) {
          success("Record has been Update successfully");
          onResetForm(addWorkGroupForm, setAddWorkGroupForm, addWorkGroupFormInitState);

          addValidationAndEnableFields(addWorkGroupForm, ["groupTypeId"]);
        } else {
          success("Record has been added successfully.");
          onResetForm(addWorkGroupForm, setAddWorkGroupForm, addWorkGroupFormInitState);
        }
        getAllWorkgropMapping(pagination); // Refetch updated data
        setWorkGroupMemberId(0);
      } else {
        if (addEditChapterData?.errorType === "EXISTS") {
          error("Board Of Directors already exists.");
          onResetForm(addWorkGroupForm, setAddWorkGroupForm, addWorkGroupFormInitState)
        } else {
          error(addEditChapterData.errorMessage);
          onResetForm(addWorkGroupForm, setAddWorkGroupForm, addWorkGroupFormInitState)
        }
      }
    }
  }, [addEditChapterLoading, addEditChapterSuccess, addEditChapterData]);

  // Handling Delete API response
  useEffect(() => {
    if (!deleteChapterLoading && deleteChapterSuccess && deleteChapterData) {
      if (!deleteChapterData.hasError) {
        success("Record Deleted Successfully.");
        getAllWorkgropMapping(pagination); // Refetch updated data
      } else {
        error(deleteChapterData.errorMessage);
      }
    }
  }, [deleteChapterLoading, addEditChapterSuccess, deleteChapterData]);

  // Populating dropdown fields once the dropdown data is fetched
  useEffect(() => {
    if (!isSelectIndividualLoading && isSelectIndividualSuccess && selectIndividualListResponse) {
      console.log(selectIndividualListResponse, "selectIndividualListResponse")
      setDataSource(selectIndividualListResponse?.itemList)
    }
  }, [isSelectIndividualLoading, isSelectIndividualSuccess, selectIndividualListResponse]);

  // Handling page change for pagination
  const handlePageChange = (page) => {
    const { pageNumber, pageSize } = page;
    const updatedRequest = {
      ...pagination,
      pageSize,
      pageNo: pageNumber,
    };
    setPagination(updatedRequest);
    getAllWorkgropMapping(updatedRequest);
  };


  // Handle edit functionality
  const handleEditClick = (data) => {
    setWorkGroupMemberId(data?.workgroupMappingId);
    const updateFormData = { ...WorkGroupMappingFormData }
    updateFormData.initialState.groupName = data?.groupName
    updateFormData.initialState.groupTypeId = data?.groupTypeName === "Committee" ? 1 : 2
    // addValidationAndEnableFields(WorkGroupMappingFormData, ["groupTypeId"]);
    setAddWorkGroupForm(updateFormData);
    removeValidationAndDisableFields(updateFormData, ["groupTypeId"]);

  };

  // Handle modal open for selecting individual record

  const handleSelectindividualClick = (data) => {
    setWorkGroupMemberId(data?.workgroupMappingId);
    let request = {
      id: data?.workgroupMappingId,
      ...pagination,
    };
    selectIndividualList(request);
    openModalSelectIndiualRecord();
  };

  const openModalSelectIndiualRecord = () => {
    SetIsEditModalOpen(!isEditModalOpen);
    setDataSource([]);
  };

  // Handle modal open for adding individual record
  const handleAddindividualClick = (data) => {
    setWorkGroupMemberId(data?.workgroupMappingId);
    SetIsEditIndividual(false)
    openModalAddIndiualRecord();
  };

  const openModalAddIndiualRecord = () => {
    SetIsAddModalOpen(!isAddModalOpen);
    getAllWorkgropMapping(pagination);
    SetEditIndividualMemberDetails(null)
    SetIsEditIndividual(false)
  };

  // Handle delete functionality
  const handleDeleteClick = (data) => {
    confirm("Delete?", "Are you sure you want to Delete?", "Delete", "Cancel").then((confirmed) => {
      if (confirmed) {
        const request = {
          Id: data?.workgroupMappingId || null,
        };
        deleteChapter(request);
      }
    });
  };

  const handleIndividualEditClick = (data) => {

    SetIsEditModalOpen(!isEditModalOpen)
    SetIsAddModalOpen(!isAddModalOpen);
    SetEditIndividualMemberDetails(data);
    SetIsEditIndividual(true)
  }

  const handleAddChapter = () => {
    let formData = addCommitteeWorkGroupRef?.current?.getFormData();
    let request = {
      WorkgroupMappingId: workgroupMappingId || null,
      GroupName: formData?.groupName || "",
      GroupTypeId: formData?.groupTypeId?.value || formData?.groupTypeId || null,
      CreatedBy: audit.adminUserId,
      CreatedAppId: audit.appId, 
    };
    if (request.GroupName && request.GroupTypeId) {
      addEditChapter(request);
    } else {

    }
  };

  const handleRowSelect = (rowIndex, rowData) => {
    console.log("Row Selected", rowIndex, rowData);
  };
  const handleColumnsChange = (fieldName, rowData, rowIndex) => {
    let newGridData = [...dataSource];
    newGridData[rowIndex] = { ...dataSource[rowIndex], ...rowData };
    setDataSource(newGridData);
  };

  const handleDeleteIndividual = () => {
    const removeIds = dataSource?.reduce((acc, item) => {
      if (item?.isSelectedRecord) {
        acc.push(item?.excoMemberId);
      }
      return acc;
    }, [])?.toString();
    let request = {
      Ids: removeIds,
      DeletedBy : audit.adminUserId,
      AppId : audit.appId,
    }
    deleteIndividualRecords(request);
  }

  var removeIds = dataSource?.reduce((acc, item) => {
    if (item?.isSelectedRecord) {
      acc.push(item?.excoMemberId);
    }
    return acc;
  }, [])

  // Action handler for grid actions
  const actionHandler = {
    SELECTINDIVIDUALRECORD: handleSelectindividualClick,
    ADDINDIVIDUALRECORD: handleAddindividualClick,
    EDIT: handleEditClick,
    DELETE: handleDeleteClick,
  };

  const actionHandlerForIndividual = {
    EDIT: handleIndividualEditClick,
  };


  return (
    <div>
      <div className="membership-application">
        <div className="form-card">

          <Suspense fallback={<DataLoader />}>
            {taskListDropDownIsSuccess && (
              <>
                <FormCreator
                  config={addWorkGroupForm}
                  ref={addCommitteeWorkGroupRef}
                  key={false}
                />
                <div className="d-flex justify-content-between">
                  <h5 className="align-self-end mb-0 mt-3">Total Count: {workgropMappingListData?.totalCount || 0}</h5>
                  <Buttons
                    onClick={handleAddChapter}
                    buttonText={`${workgroupMappingId > 0 ? 'Update/Save Grouping' : 'Add/Save Grouping'}`}
                    buttonTypeClassName="theme-btn btn mt-auto form-btn-minus"
                    isLoading={false}
                  />
                </div>
              </>
            )}
          </Suspense>
         
          {/* Workgroup List */}
          <div className="table-100 mt-3">
            <FinalMolGrid
              ref={committeWorkGroupRef}
              configuration={WorkgroupMappingList}
              dataSource={workGroupListData}
              allowPagination={true}
              pagination={pagination}
              onActionChange={actionHandler}
              onPageChange={handlePageChange}
            />
          </div>
          </div>
          {/* Add Workgroup Form */}

        </div>
     

      {/* Select Individual Modal */}
      {isEditModalOpen && (
        <CenterModel
          modelTitle={"Select Individual Record"}
          show={"test"}
          handleClose={openModalSelectIndiualRecord}
          onUpload={openModalSelectIndiualRecord}
        >
          <FinalMolGrid
            ref={selectIndividualListRef}
            configuration={SelectIndividualMemberData}
            dataSource={dataSource}
            onRowSelect={handleRowSelect}
            onColumnChange={handleColumnsChange}
            onActionChange={actionHandlerForIndividual}
            allowPagination={true}
          />
          <div className="btn-sec d-flex justify-content-between mt-5">
            <Buttons
              onClick={openModalSelectIndiualRecord}
              buttonText={"Cancel"}
              buttonTypeClassName="dark-btn btn mt-auto"
              isLoading={false}
            />
            {removeIds?.length > 0 && <Buttons
              onClick={handleDeleteIndividual}
              buttonText={"Remove"}
              buttonTypeClassName="theme-btn btn mt-auto"
              isLoading={isDeleteRecordLoading}
            />}
          </div>
        </CenterModel>
      )}

      {/* Add Individual Modal */}
      {isAddModalOpen && (
        <CenterModel
          modelTitle={"Add EXCO Member"}
          show={"test"}
          handleClose={openModalAddIndiualRecord}
          onUpload={openModalAddIndiualRecord}
        >
          <AddIndividualRecord
            ref={addIndividualRef}
            workgroupMappingId={workgroupMappingId}
            closeModalPopup={openModalAddIndiualRecord}
            editIndividualMemberDetails={editIndividualMemberDetails}
            isEdit={isEditIndividual}
          />
        </CenterModel>
      )}
    </div>
  );
};

export default CommitteeWorkgroupMappingForm;
